import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLayout } from '../../../_metronic/layout/core'
import { getDetailExperience } from '../../services/teamExperienceService'
import { useHistory } from 'react-router-dom';
import { EXPERIENCE_STATUS, EXPERIENCE_STATUS_LIST } from '../teamExperiences/models/ExperienceStatus';
import { KTSVG } from '../../../_metronic/helpers';
import { RootState } from '../../../setup';
import { UserModel } from '../../modules/auth/models/UserModel';
import { shallowEqual, useSelector } from 'react-redux';
import { CalendarWrapper } from '../calendar/CalendarWrapper';
import { getTickets } from "../../../../src/app/services/ticketService";
import { deleteTask, getTasks, getTasksSummary, updateTask } from "../../../../src/app/services/adminTicketsService";
import { Card6 } from '../../../_metronic/partials/content/cards/Card6';
import { formatDateStrShort } from '../../helpers/FormatDate';
import { GenericDropdownIcon } from '../../hooks/utils/GenericDropdownIcon';
import { TaskModal } from './components/TaskModal';
import { useConfirmAction } from '../../hooks/utils/useConfirmAction';
import { showErrorAlert } from '../../helpers/alert';
import { TaskFilterBar } from './components/TaskFilterBar';
import {
    AdminTask,
    applyTaskFilters,
    deriveFilterOptions,
    EMPTY_FILTERS,
    TaskFilters,
} from './taskFilters';
import { DatatableComponent } from '../../helpers/DatatableComponent';
import TooltipMaxLength from '../../hooks/utils/TooltipMaxLength';
import { defaultProfileImage } from '../../utils/constants';

// The task list is small (admin task board); load a generous page so status/staff/member-or-partner/
// activity filtering happens correctly across the whole set in memory, driving both views.
const TASK_FETCH_LIMIT = 200;

const STATUS_CLASS: Record<string, string> = {
    pending: 'support-tickets-status--pending',
    in_progress: 'support-tickets-status--in-progress',
    resolved: 'support-tickets-status--resolved',
    cancelled: 'support-tickets-status--cancelled',
}

const personName = (p: any): string => {
    const name = `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim()
    return name || p?.email || `#${p?.id}`
}

const relatedItemSummary = (t: any): string => {
    const members = (t.relatedMembers ?? []).map(personName)
    const partners = (t.relatedPartners ?? []).map(personName)
    const experiences = (t.relatedExperiences ?? []).map((e: any) => e.title ?? `#${e.id}`)
    const parts: string[] = []
    if (experiences.length) parts.push(`Experience: ${experiences.join(', ')}`)
    if (partners.length) parts.push(`Partner: ${partners.join(', ')}`)
    if (members.length) parts.push(`Members: ${members.join(', ')}`)
    return parts.join(' · ') || '—'
}

const renderStatusPill = (row: any) => {
    const cls = row.status ? STATUS_CLASS[row.status] ?? '' : ''
    return (
        <span className={`support-tickets-status ${cls}`}>
            {row.statusDisplay ?? row.status ?? '—'}
        </span>
    )
}

const renderNobilisAdmin = (row: any) => (
    <div className='support-tickets-table__admin card-profile-avatar'>
        <img
            src={defaultProfileImage}
            alt={row.createdByName ?? ''}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = defaultProfileImage }}
        />
        <span>{row.createdByName ?? '—'}</span>
    </div>
)

const renderNoteCell = (row: any) => (
    <TooltipMaxLength
        text={row.title ? `${row.title}${row.notes ? ' — ' + row.notes : ''}` : (row.notes ?? '—')}
        maxLength={120}
        maxWidth={420}
        position='bottom'
    />
)

const renderRelatedCell = (row: any) => (
    <TooltipMaxLength
        text={relatedItemSummary(row)}
        maxLength={70}
        maxWidth={360}
        position='bottom'
    />
)

interface SearchProps {
    limit:number;
    offset:number;
    status:string;
    search?:string;
    type?:string;
}

const AdminDashboardPage: React.FC = () => {
    const limit = 10;
    const [ page, setPage ] = useState(1);
    const [ count, setCount ] = useState(1);

    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const loggedUser = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
    const [showTicketModal, setShowTicketModal] = useState(false)
    const [ ticketId, setTicketId ] = useState(0)
    const offset = (page - 1) * limit;

    const [isLoader, setIsLoader] = useState(true);
    const [messageLoader, setMessageLoader] = useState("Loading...")

    const [suggestedExperiences, setSuggestedExperiences] = useState('')
    const [newUsersWaitlist, setNewUsersWaitlist] = useState('')
    const [newExperiencesToReview, setNewExperiencesToReview] = useState('')    
    const [introductionsToReview, setIntroductionsToReview] = useState('')
    const [expertsToReview, setExpertsToReview] = useState('')

    const [tabs, setTabs] = useState<any>()
    const [activeTab, setActiveTab] = useState()
    const [searchTerm, setSearchTerm] = useState('')
    // Raw fetched tasks; the visible open/done lists + calendar are all derived from these after
    // applying the shared filters (so both views always show the same filtered set).
    const [allTasks, setAllTasks] = useState<AdminTask[]>([]);
    const [filters, setFilters] = useState<TaskFilters>(EMPTY_FILTERS);
    const [ tickets, setTickets ] = useState([])
    const [totalRows, setTotalRows] = useState<any>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const history = useHistory()
    const confirm = useConfirmAction();

    const [showCompletedTasks, setShowCompletedTasks] = useState(false)

    // One filtered array feeds BOTH views; the list splits it into open/done, the calendar shows all.
    const filteredTasks = useMemo(() => applyTaskFilters(allTasks, filters), [allTasks, filters]);
    const tasks = useMemo(
        () => filteredTasks.filter((t) => t.status === 'pending' || t.status === 'in_progress'),
        [filteredTasks]
    );
    const tasksDone = useMemo(
        () => filteredTasks.filter((t) => t.status === 'resolved' || t.status === 'cancelled'),
        [filteredTasks]
    );
    // Filter dropdown options come from the full loaded set (not the filtered one), so choosing one
    // filter doesn't empty the others.
    const filterOptions = useMemo(() => deriveFilterOptions(allTasks), [allTasks]);

    const handleFilterChange = (patch: Partial<TaskFilters>) =>
        setFilters((prev) => ({ ...prev, ...patch }));
    const handleClearFilters = () => setFilters(EMPTY_FILTERS);

    const experienceStatusList:any = [
      { id: EXPERIENCE_STATUS.PUBLISHED, code: 'active', name: 'List' },
      { id: EXPERIENCE_STATUS.PRE_LAUNCH, code: 'preLaunch', name: 'Calendar' },
    ]
    
    const fetchSummary = async (showLoader:boolean=false) => {
        try {
            const summary_ = await getTasksSummary();
            setIsLoader(false);
            setSuggestedExperiences(summary_.suggestedExperiences);
            setNewUsersWaitlist(summary_.newUsersWaitlist);
            setNewExperiencesToReview(summary_.newExperiencesToReview);
            setIntroductionsToReview(summary_.introductionsToReview);
            setExpertsToReview(summary_.expertsToReview);
        } catch (error) {
            setIsLoader(false);
            history.goBack();
        }
       setIsLoader(false);
    }

    const fetchData = async (params?:Partial<SearchProps>,showLoader:boolean=false) => {
        try {
            // Load a full page (server searches title/notes); status/staff/member-or-partner/activity
            // filtering is applied in memory across the whole set. NB: the List/Calendar view tab id
            // is NOT a task status, so it is intentionally not sent as a `status` query param.
            const tickets_ = await getTasks({ limit: TASK_FETCH_LIMIT, offset: 0, search: params?.search ?? searchTerm });
            setAllTasks(tickets_?.results ?? []);
            setIsLoader(false);
            setTotalRows(Number(tickets_?.count));
        } catch (error) {
            setIsLoader(false);
            history.goBack();
        }
       setIsLoader(false);
    }

    useEffect(() => {
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: true },
            aside: { ...config.aside, display: true },
            toolbar: { ...config.toolbar, display: false },
            footer: { ...config.footer, display: true },
        })        
        return () => {
            setLayout(restoreRef.current)
        }
    }, [])

    useEffect(() => {
        const handler = setTimeout(() => {
            setActiveTab(1);
            fetchData({ limit, offset: 0, status: (activeTab ?? '1'), search: searchTerm }); 
            fetchSummary();           
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const totalPages = totalRows ? Math.ceil(totalRows / limit) : 1;

    const changeTab = async (status:string) => {
        setActiveTab(status);
        await fetchData({ limit, offset: 0, status });
    }

    const handleCloseTicket = () => {
        setTicketId(0);
        setShowTicketModal(false);
        fetchData({ limit, offset: 0, status: (activeTab ?? '1'), search: searchTerm });
    }

    const handleOnEdit = (id:number) => {
        setTicketId(id);
        setShowTicketModal(true);
    }

    const handleOnDelete = async (id:number) => {
        let isConfirm = await confirm({ title: 'Delete Task', message: 'Are you sure you want to delete this task?' });
        if(isConfirm) {
            setAllTasks((prev) => prev.filter((t) => Number(t.id) !== Number(id)));
            let isDeleted = await deleteTask(id);
            if(isDeleted?.status == 204){
                fetchData({ limit, offset: 0, status: (activeTab ?? '1'), search: searchTerm });
                showErrorAlert({ title: '', message: 'Your task has been deleted successfully.' });
            } else {
                fetchData({ limit, offset: 0, status: (activeTab ?? '1'), search: searchTerm });
            }
        }
    }

    const handleOnMarkAsDone = async (id:number) => {
        let isConfirm = await confirm({ title: 'Mark as Done', message: 'Are you sure you want to mark this task as done?' });
        if(isConfirm) {
            setAllTasks((prev) => prev.map((t) => Number(t.id) === Number(id) ? { ...t, status: 'resolved', statusDisplay: 'Resolved' } : t));
            let isUpdate = await updateTask(id,{status: 'resolved'});
            if(isUpdate?.status == 200){
                fetchData({ limit, offset: 0, status: (activeTab ?? '1'), search: searchTerm });
                showErrorAlert({ title: '', message: 'Your task has been marked as done.' });
            } else {
                fetchData({ limit, offset: 0, status: (activeTab ?? '1'), search: searchTerm });
            }
        }
    }

    const handleOnUnmarkAsCompleted = async (id: number) => {
        let isConfirm = await confirm({ title: 'Unmark as Completed', message: 'Are you sure you want to unmark this task as completed?' });
        if(isConfirm) {
            setAllTasks((prev) => prev.map((t) => Number(t.id) === Number(id) ? { ...t, status: 'in_progress', statusDisplay: 'In progress' } : t));
            let isUpdate = await updateTask(id, {status: 'in_progress'});
            if(isUpdate?.status == 200){
                fetchData({ limit, offset: 0, status: (activeTab ?? '1'), search: searchTerm });
                showErrorAlert({ title: '', message: 'Your task has been unmarked as completed.' });
            } else {
                fetchData({ limit, offset: 0, status: (activeTab ?? '1'), search: searchTerm });
            }
        }
    }

    const openColumns = useMemo(() => [
        {
            key: 'dueDate',
            label: 'DATE',
            getValue: (row: any) => <>{formatDateStrShort(row.dueDate)}</>
        },
        {
            key: 'statusDisplay',
            label: 'STATUS',
            getValue: renderStatusPill,
        },
        {
            key: 'title',
            label: 'NOTE',
            getValue: renderNoteCell,
        },
        {
            key: 'relatedItem',
            label: 'RELATED ITEM',
            getValue: renderRelatedCell,
        },
        {
            key: 'createdByName',
            label: 'NOBILIS ADMIN',
            getValue: renderNobilisAdmin,
        },
        {
            key: 'actions',
            label: 'ACTIONS',
            getValue: (u: any) => (
                <div
                    className='table__cell--bold w-100 tap-flex-center'
                    style={{height:'64px', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>
                    <GenericDropdownIcon
                        options={[
                            {
                                id: 1,
                                label: 'Mark as done',
                                onClick: () => handleOnMarkAsDone(Number(u.id))
                            },
                            {
                                id: 2,
                                label: 'Delete',
                                onClick: () => handleOnDelete(Number(u.id))
                            },
                            {
                                id: 3,
                                label: 'Edit',
                                onClick: () => handleOnEdit(Number(u.id))
                            }
                        ]}
                        placeholder="⋅⋅⋅"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.id}
                    />
                </div>
            )
        }
    ], [handleOnMarkAsDone, handleOnDelete, handleOnEdit]);

    const completedColumns = useMemo(() => [
        {
            key: 'dueDate',
            label: 'DATE',
            getValue: (row: any) => <>{formatDateStrShort(row.dueDate)}</>
        },
        {
            key: 'statusDisplay',
            label: 'STATUS',
            getValue: renderStatusPill,
        },
        {
            key: 'title',
            label: 'NOTE',
            getValue: renderNoteCell,
        },
        {
            key: 'relatedItem',
            label: 'RELATED ITEM',
            getValue: renderRelatedCell,
        },
        {
            key: 'createdByName',
            label: 'NOBILIS ADMIN',
            getValue: renderNobilisAdmin,
        },
        {
            key: 'actions',
            label: 'ACTIONS',
            getValue: (row: any) => (
                <div className="w-100 tap-flex-center" style={{height:'64px', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>
                    <GenericDropdownIcon
                        options={[
                            {
                                id: 1,
                                label: 'Unmark as completed',
                                onClick: () => handleOnUnmarkAsCompleted(Number(row.id))
                            }
                        ]}
                        placeholder="⋅⋅⋅"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.id}
                    />
                </div>
            )
        },
    ], [handleOnUnmarkAsCompleted]);

    return (<div className='admin-experience-page admin-experience-page--dashboard'>
        <div className='admin-experience-page__container'>
            <div className='w-100 d-flex flex-row justify-content-between align-items-center'>
                <div style={{width: '100%', color: '#151515', fontSize: 32, fontFamily: 'Oswald', fontWeight: '500', wordWrap: 'break-word'}}>Welcome, {loggedUser?.firstName}!</div>
            </div>

            <div className={`row g-6 g-xl-9 mb-6 mb-xl-9 ${activeTab == '1' ? 'd-flex' : 'd-none'}`}>
                <div className='col-xl'>
                    <Card6
                        number={suggestedExperiences}
                        title='Suggested Experiences'
                        button='Manage'
                        onClick={() => history.push('/experience/admin?tab=Suggested')}
                    />
                </div>
                <div className='col-xl'>
                    <Card6
                        number={newUsersWaitlist}
                        title='New users in the waitlist'
                        button='Manage'
                        onClick={() => history.push('/waitinglist')}
                    />
                </div>
                <div className='col-xl'>
                    <Card6
                        number={newExperiencesToReview}
                        title='New experiences to review'
                        button='Manage'
                        onClick={() => history.push('/experience/admin?tab=Members')}
                    />
                </div>
                <div className='col-xl'>
                    <Card6
                        number={introductionsToReview}
                        title='Introductions to review'
                        button='Manage'
                        onClick={() => history.push('/introductions')}
                    />
                </div>
                <div className='col-xl'>
                    <Card6
                        number={expertsToReview}
                        title='Experts to review'
                        button='Manage'
                    />
                </div>
            </div>

            <div className="card" style={{flexDirection:'row'}}>
                <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-bottom-0">
                    {experienceStatusList.map((item:any,index:number)=>(<div className='d-flex flex-row' key={`tabs-experiences-${index}`}>
                        <li className="nav-item">
                            <a className={`nav-link text-capitalize ${activeTab === item.id ? "active" : ""}`}
                                onClick={() => changeTab(item.id)}>
                                {item.name}
                                {tabs && (<div className={`badge-wrapper ${tabs && tabs[item.code] && Number(tabs[item.code]) > 0 ? 'd-flex' : 'd-none'}`}>
                                    <div className='badge-count'>{tabs[item.code]}</div>
                                </div>)}
                            </a>
                        </li>
                        <li className={`separator-vertical ${index < experienceStatusList.length-1 ? 'd-flex' : 'd-none'}`}>
                            <hr />
                        </li>
                    </div>))}
                </ul>
                <button className="tap-add-experience-2-btn-secondary" onClick={()=>setShowTicketModal(true)} style={{marginLeft:'auto'}}>
                    <KTSVG path='/media/svg/nobilis/calendar-edit.svg' />
                    <span>&nbsp;&nbsp;Add Task</span>
                </button>
            </div>

            <TaskFilterBar
                filters={filters}
                options={filterOptions}
                onChange={handleFilterChange}
                onClear={handleClearFilters}
                search={searchTerm}
                onSearchChange={setSearchTerm}
                count={filteredTasks.length}
            />

            {activeTab == '1' && (
                <>
                    <DatatableComponent
                        columns={openColumns}
                        tableClassName='admin-experience-page-table-responsive'
                        textNoWrap
                        data={tasks}
                        currentPage={1}
                        totalPages={1}
                        onPageChange={() => {}}
                    />        
                    <a className='text-hover-primary' style={{justifyItems: 'center', display:'grid'}}>
                        <div onClick={() => setShowCompletedTasks(!showCompletedTasks)} style={{color: '#151515', fontSize: 12, fontFamily: 'Satoshi', fontWeight: '500', textDecoration: 'underline', textTransform: 'uppercase', lineHeight: 16.80, wordWrap: 'break-word', cursor:'pointer'}}>
                            {showCompletedTasks ? 'HIDE COMPLETED TASKS' : 'SHOW COMPLETED TASKS'}
                        </div>
                    </a> 
                    {showCompletedTasks &&
                        <DatatableComponent
                            columns={completedColumns}
                            tableClassName='admin-experience-page-table-responsive'
                            textNoWrap
                            data={tasksDone}
                            currentPage={1}
                            totalPages={1}
                            onPageChange={() => {}}
                        />
                    }
                </>
            )}
            {activeTab == '2' && (
                <div className='admin-experience-page__content' style={{ width: '100%', overflow: 'auto' }}>  
                    <div className={`${activeTab == '2' ? 'd-grid' : 'd-none'}`}>
                        <CalendarWrapper tasks={filteredTasks} onTaskClick={handleOnEdit}/>
                    </div>
                </div>
            )}
            {/* <LoaderOverlay visible={isLoader} message={messageLoader} /> */}
        </div>
        <TaskModal show={showTicketModal} onHide={handleCloseTicket} ticketId={ticketId} />
    </div>)
}

export default AdminDashboardPage
